import {
  useQuery,
  type UseQueryOptions,
  type UseQueryResult,
  type QueryKey,
} from '@tanstack/react-query';
import service from '../service';
import type { TicketedEvent } from '../types/ticketedEvents';

type TicketedEventResponse = TicketedEvent;
type DefaultError = unknown;

type UseTicketedEventParams = {
  eventId?: string;
  /**
   * Comma-separated list of relations, e.g. 'venue,featuredImage,images'
   */
  include?: string;
  /**
   * Filter parameters as key-value pairs.
   * Keys should be in the format 'filter[field]' or 'filter[relation.field]'
   * Example: { 'filter[venue.deletedAt]': 'null' }
   */
  filters?: Record<string, string>;
  /** Override default enabled behavior */
  enabled?: boolean;
};

/**
 * useTicketedEvent
 *
 * @example
 * const eventQuery = useTicketedEvent(
 *   {
 *     eventId,
 *     include: 'venue,featuredImage,images',
 *   },
 *   {
 *     staleTime: 60_000,
 *   },
 * );
 */
export function useTicketedEvent<
  TData = TicketedEventResponse,
  TError = DefaultError,
>(
  { eventId, include, filters, enabled = true }: UseTicketedEventParams = {},
  options?: Omit<
    UseQueryOptions<TicketedEventResponse, TError, TData, QueryKey>,
    'queryKey' | 'queryFn' | 'enabled'
  >,
): UseQueryResult<TData, TError> {
  const queryKey: QueryKey = ['ticketedEvent', eventId, include, filters];

  return useQuery<TicketedEventResponse, TError, TData>({
    queryKey,
    // don't run until we actually have an ID
    enabled: enabled && !!eventId,
    queryFn: async () => {
      if (!eventId) {
        throw new Error('eventId is required to fetch a ticketed event');
      }

      return service.getTicketedEvent(eventId, {
        ...(filters || {}),
        ...(include ? { include } : {}),
      });
    },
    ...(options as any),
  });
}
